import {
  type AuthProvider,
  PASSWORD_MIN_LENGTH,
  type ZxcvbnScore,
} from '@nest-react/domain';
import { type AppLocale } from '@nest-react/lib';
import {
  type ReactElement,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { Trans, useTranslation } from 'react-i18next';
import {
  Navigate,
  useNavigate,
  useParams,
  useSearchParams,
} from 'react-router-dom';
import { Eye, EyeOff } from 'lucide-react';
import { useAuth, useWorkspace } from '~/app/providers';
import { BrandLogo } from '~/components/ui/BrandLogo';
import { ThemeToggle } from '~/components/ui/ThemeToggle';
import { Button, Label, SegmentedControl, TextField } from '~/components/ui';
import { useRegionalApi } from '~/app/region';
import type { RegionalApiClient } from '~/app/regionalApiClient';
import { PasswordStrengthIndicator } from './PasswordStrengthIndicator';
import { loadPasswordPolicy } from './passwordPolicyLoader';

const LOGIN_LOCALE_OPTIONS: { value: AppLocale; label: string }[] = [
  { value: 'en', label: 'EN' },
  { value: 'ru', label: 'RU' },
  { value: 'zh-Hans', label: 'ZH' },
];

type AuthMode = 'login' | 'register';
type PasswordEvaluation =
  | {
      inputKey: string;
      status: 'failed';
    }
  | {
      inputKey: string;
      score: ZxcvbnScore;
      status: 'ready';
      valid: boolean;
    };

const PASSWORD_STRENGTH_LABEL_KEY: Record<
  ZxcvbnScore,
  | 'passwordStrength.score0'
  | 'passwordStrength.score1'
  | 'passwordStrength.score2'
  | 'passwordStrength.score3'
  | 'passwordStrength.score4'
> = {
  0: 'passwordStrength.score0',
  1: 'passwordStrength.score1',
  2: 'passwordStrength.score2',
  3: 'passwordStrength.score3',
  4: 'passwordStrength.score4',
};

const mapAuthError = (
  error: string | undefined,
  t: (key: string) => string
): string => {
  if (!error) return t('failed');
  if (error === 'An account with this email already exists') {
    return t('emailAlreadyExists');
  }
  if (error === 'Invalid email or password') {
    return t('invalidCredentials');
  }
  if (error === 'Password must be at least 10 characters') {
    return t('passwordTooShort');
  }
  if (error === 'Password is too weak') {
    return t('passwordTooWeak');
  }
  return error;
};

const authRequest = async (
  api: RegionalApiClient,
  path: string,
  body: Record<string, string>
): Promise<{ ok: boolean; error?: string }> => {
  try {
    await api.request(path, {
      method: 'POST',
      credentials: 'include',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify(body),
    });
    return { ok: true };
  } catch (reason) {
    return {
      ok: false,
      error: reason instanceof Error ? reason.message : 'Network error',
    };
  }
};

export const LoginPage = (): ReactElement => {
  const api = useRegionalApi();
  const { t } = useTranslation('auth');
  const { t: tc } = useTranslation('common');
  const { session, signIn, refresh } = useAuth();
  const { locale, setLocale, companies } = useWorkspace();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const returnTo = searchParams.get('returnTo') || '/';
  const [mode, setMode] = useState<AuthMode>('login');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [passwordVisible, setPasswordVisible] = useState(false);
  const [confirmPasswordVisible, setConfirmPasswordVisible] = useState(false);
  const [displayName, setDisplayName] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [code, setCode] = useState('');
  const [emailStep, setEmailStep] = useState<'email' | 'code'>('email');
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState('');
  const [passwordEvaluation, setPasswordEvaluation] =
    useState<PasswordEvaluation | null>(null);
  const [passwordEvaluationAttempt, setPasswordEvaluationAttempt] = useState(0);
  const [providers, setProviders] = useState<Record<AuthProvider, boolean>>({
    google: false,
    vk: false,
  });

  const passwordUserInputs = useMemo(
    () => [email, displayName],
    [email, displayName]
  );
  const passwordInputKey = useMemo(
    () => JSON.stringify([password, ...passwordUserInputs]),
    [password, passwordUserInputs]
  );

  useEffect(() => {
    if (mode !== 'register' || !password) return undefined;

    let current = true;
    void loadPasswordPolicy().then(
      ({ assessPassword, validatePasswordForRegistration }) => {
        if (!current) return;
        setPasswordEvaluation({
          inputKey: passwordInputKey,
          score: assessPassword(password, passwordUserInputs).score,
          status: 'ready',
          valid: validatePasswordForRegistration(password, passwordUserInputs)
            .valid,
        });
      },
      () => {
        if (!current) return;
        setPasswordEvaluation({ inputKey: passwordInputKey, status: 'failed' });
      }
    );
    return () => {
      current = false;
    };
  }, [
    mode,
    password,
    passwordEvaluationAttempt,
    passwordInputKey,
    passwordUserInputs,
  ]);

  const currentPasswordEvaluation =
    passwordEvaluation?.inputKey === passwordInputKey
      ? passwordEvaluation
      : null;
  const passwordScore =
    currentPasswordEvaluation?.status === 'ready'
      ? currentPasswordEvaluation.score
      : undefined;
  const registrationPasswordValid =
    currentPasswordEvaluation?.status === 'ready'
      ? currentPasswordEvaluation.valid
      : false;
  const passwordValidationUnavailable =
    currentPasswordEvaluation?.status === 'failed';
  const passwordStrengthLabel =
    passwordScore === undefined
      ? ''
      : t(PASSWORD_STRENGTH_LABEL_KEY[passwordScore]);

  useEffect(() => {
    void (async () => {
      try {
        const items = await api.request<
          {
            provider: AuthProvider;
            configured: boolean;
          }[]
        >('/auth/providers');
        setProviders(
          Object.fromEntries(
            items.map(item => [item.provider, item.configured])
          ) as Record<AuthProvider, boolean>
        );
      } catch {
        // Login remains visible with unavailable providers disabled.
      }
    })();
  }, [api]);

  const handleSubmit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();
      setError('');
      if (!email.trim()) {
        setError(t('emailRequired'));
        return;
      }
      if (!password) {
        setError(t('passwordRequired'));
        return;
      }
      if (mode === 'register') {
        let validatePasswordForRegistration: (
          value: string,
          userInputs?: string[]
        ) => { valid: boolean; reason?: 'too_short' | 'too_weak' };
        try {
          ({ validatePasswordForRegistration } = await loadPasswordPolicy());
        } catch {
          setError(t('passwordValidationUnavailable'));
          return;
        }
        const validation = validatePasswordForRegistration(
          password,
          passwordUserInputs
        );
        if (!validation.valid) {
          setError(
            validation.reason === 'too_short'
              ? t('passwordTooShort')
              : t('passwordTooWeak')
          );
          return;
        }
        if (password !== confirmPassword) {
          setError(t('passwordsDoNotMatch'));
          return;
        }
      }
      setLoading(true);
      const path = mode === 'register' ? '/auth/register' : '/auth/login';
      const body: Record<string, string> = { email, password };
      if (mode === 'register') {
        body.displayName = displayName.trim() || email.split('@')[0];
      }
      const result = await authRequest(api, path, body);
      setLoading(false);
      if (result.ok) {
        await refresh();
        navigate(returnTo);
      } else {
        setError(mapAuthError(result.error, t));
      }
    },
    [
      api,
      email,
      password,
      confirmPassword,
      displayName,
      mode,
      passwordUserInputs,
      t,
      refresh,
      navigate,
      returnTo,
    ]
  );

  const requestEmailCode = useCallback(async (): Promise<void> => {
    setBusy(true);
    setError('');
    setMessage('');
    try {
      await authRequest(api, '/auth/email/request-code', { email });
      setEmailStep('code');
      setMessage(t('codePrinted'));
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : t('emailFailed'));
    } finally {
      setBusy(false);
    }
  }, [email, t]);

  const verifyEmailCode = useCallback(async (): Promise<void> => {
    setBusy(true);
    setError('');
    try {
      await authRequest(api, '/auth/email/verify', { email, code });
      await refresh();
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : t('emailFailed'));
    } finally {
      setBusy(false);
    }
  }, [email, code, t, refresh]);

  const toggleMode = useCallback(() => {
    setMode(prev => (prev === 'login' ? 'register' : 'login'));
    setConfirmPassword('');
    setPasswordVisible(false);
    setConfirmPasswordVisible(false);
    setError('');
  }, []);

  if (session)
    return (
      <Navigate
        replace
        to={
          companies.length
            ? returnTo
            : `/onboarding?returnTo=${encodeURIComponent(returnTo)}`
        }
      />
    );

  return (
    <main className="relative grid min-h-screen overflow-hidden bg-surface text-foreground lg:grid-cols-[1.15fr_0.85fr]">
      <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_10%_20%,rgb(139_92_246_/_0.12),transparent_32rem)]" />
      <section className="relative hidden border-r border-border-subtle p-12 lg:flex lg:flex-col">
        <BrandLogo />
        <div className="my-auto max-w-2xl">
          <p className="mb-5 font-mono text-[11px] uppercase tracking-[0.25em] text-primary-text">
            {t('eyebrow')}
          </p>
          <h1
            className={
              locale === 'zh-Hans'
                ? 'whitespace-nowrap text-[clamp(2.25rem,3vw,3.75rem)] font-semibold leading-[0.98] tracking-[-0.06em] text-foreground'
                : 'text-6xl font-semibold leading-[0.98] tracking-[-0.06em] text-foreground'
            }
          >
            {t('title')}
          </h1>
          <p className="mt-7 max-w-xl text-lg leading-8 text-muted">
            {t('subtitle')}
          </p>
          <div className="mt-12 grid grid-cols-3 gap-px overflow-hidden rounded-2xl border border-border bg-border">
            <Metric value="12x" label={tc('metricCreative')} />
            <Metric value="RU/CIS" label={tc('metricMarket')} />
            <Metric value="24/7" label={tc('metricAgent')} />
          </div>
        </div>
        <p className="font-mono text-[10px] uppercase tracking-widest text-subtle">
          {tc('paidBeta')}
        </p>
      </section>
      <section className="relative flex min-h-screen items-center justify-center p-5 sm:p-10">
        <div className="w-full max-w-md">
          <div className="mb-12 flex items-center justify-between gap-3 lg:justify-end">
            <div className="lg:hidden">
              <BrandLogo />
            </div>
            <div className="ml-auto flex items-center gap-2">
              <ThemeToggle />
              <SegmentedControl
                label={tc('language')}
                onChange={value => setLocale(value as AppLocale)}
                options={LOGIN_LOCALE_OPTIONS}
                value={locale}
              />
            </div>
          </div>
          <div className="rounded-3xl border border-border bg-elevated p-6 shadow-[var(--studio-shadow)] sm:p-8">
            <div className="mb-8">
              <span className="mb-5 block size-2 rounded-full bg-primary shadow-[0_0_24px_8px_rgb(139_92_246_/_0.2)]" />
              <h2 className="text-2xl font-semibold tracking-tight text-foreground">
                {mode === 'register' ? t('registerTitle') : t('loginTitle')}
              </h2>
              <p className="mt-2 text-sm leading-6 text-muted">
                {mode === 'register'
                  ? t('registerSubtitle')
                  : t('loginSubtitle')}
              </p>
            </div>
            <form onSubmit={e => void handleSubmit(e)} className="grid gap-4">
              {mode === 'register' && (
                <Label htmlFor="displayName">
                  {t('displayName')}
                  <TextField
                    autoComplete="name"
                    id="displayName"
                    onChange={e => setDisplayName(e.target.value)}
                    type="text"
                    value={displayName}
                  />
                </Label>
              )}
              <Label htmlFor="email">
                {t('email')}
                <TextField
                  autoComplete="email"
                  id="email"
                  onChange={e => setEmail(e.target.value)}
                  required
                  type="email"
                  value={email}
                />
              </Label>
              <Label htmlFor="password">
                {t('password')}
                <div className="relative">
                  <TextField
                    autoComplete={
                      mode === 'register' ? 'new-password' : 'current-password'
                    }
                    className="pr-11"
                    id="password"
                    minLength={
                      mode === 'register' ? PASSWORD_MIN_LENGTH : undefined
                    }
                    name="password"
                    onChange={e => setPassword(e.target.value)}
                    required
                    type={passwordVisible ? 'text' : 'password'}
                    value={password}
                  />
                  <button
                    aria-label={
                      passwordVisible ? t('hidePassword') : t('showPassword')
                    }
                    aria-pressed={passwordVisible}
                    className="absolute inset-y-0 right-0 flex w-11 items-center justify-center text-subtle transition-colors hover:text-foreground focus-visible:outline-2 focus-visible:outline-offset-[-4px] focus-visible:outline-primary"
                    onClick={() => setPasswordVisible(visible => !visible)}
                    type="button"
                  >
                    {passwordVisible ? (
                      <EyeOff aria-hidden="true" className="size-4" />
                    ) : (
                      <Eye aria-hidden="true" className="size-4" />
                    )}
                  </button>
                </div>
              </Label>
              {mode === 'register' && (
                <p className="-mt-2 text-xs text-subtle">
                  {t('passwordMinLength')}
                </p>
              )}
              {mode === 'register' && passwordScore !== undefined && (
                <PasswordStrengthIndicator
                  label={passwordStrengthLabel}
                  score={passwordScore}
                />
              )}
              {mode === 'register' && passwordValidationUnavailable ? (
                <div
                  className="flex items-center justify-between gap-3 rounded-lg bg-red-500/10 px-3 py-2 text-xs text-red-400"
                  role="alert"
                >
                  <span>{t('passwordValidationUnavailable')}</span>
                  <button
                    className="shrink-0 font-semibold underline underline-offset-2"
                    onClick={() =>
                      setPasswordEvaluationAttempt(attempt => attempt + 1)
                    }
                    type="button"
                  >
                    {t('retry')}
                  </button>
                </div>
              ) : null}
              {mode === 'register' && (
                <Label htmlFor="confirmPassword">
                  {t('confirmPassword')}
                  <div className="relative">
                    <TextField
                      autoComplete="new-password"
                      className="pr-11"
                      id="confirmPassword"
                      minLength={PASSWORD_MIN_LENGTH}
                      name="confirmPassword"
                      onChange={e => setConfirmPassword(e.target.value)}
                      required
                      type={confirmPasswordVisible ? 'text' : 'password'}
                      value={confirmPassword}
                    />
                    <button
                      aria-label={
                        confirmPasswordVisible
                          ? t('hidePassword')
                          : t('showPassword')
                      }
                      aria-pressed={confirmPasswordVisible}
                      className="absolute inset-y-0 right-0 flex w-11 items-center justify-center text-subtle transition-colors hover:text-foreground focus-visible:outline-2 focus-visible:outline-offset-[-4px] focus-visible:outline-primary"
                      onClick={() =>
                        setConfirmPasswordVisible(visible => !visible)
                      }
                      type="button"
                    >
                      {confirmPasswordVisible ? (
                        <EyeOff aria-hidden="true" className="size-4" />
                      ) : (
                        <Eye aria-hidden="true" className="size-4" />
                      )}
                    </button>
                  </div>
                </Label>
              )}
              {error && (
                <p className="rounded-lg bg-red-500/10 px-3 py-2 text-xs text-red-400">
                  {error}
                </p>
              )}
              <Button
                type="submit"
                className="w-full bg-primary text-white hover:bg-primary-300 hover:text-black"
                disabled={
                  loading || (mode === 'register' && !registrationPasswordValid)
                }
              >
                {loading
                  ? '...'
                  : mode === 'register'
                    ? t('register')
                    : t('login')}
              </Button>
            </form>
            <div className="mt-5 flex items-center gap-3">
              <div className="h-px flex-1 bg-border-subtle" />
              <span className="text-[11px] text-subtle">
                {t('orContinueWith')}
              </span>
              <div className="h-px flex-1 bg-border-subtle" />
            </div>
            <div className="mt-5 grid gap-3">
              <Button
                className="w-full bg-elevated text-foreground hover:bg-hover"
                disabled={!providers.google}
                onClick={() => void signIn('google', returnTo)}
                variant="secondary"
              >
                G&nbsp;&nbsp; {t('google')}
              </Button>
              <Button
                className="w-full bg-[#07f] text-white hover:bg-[#1685ff]"
                disabled={!providers.vk}
                onClick={() => void signIn('vk', returnTo)}
              >
                VK&nbsp;&nbsp; {t('vk')}
              </Button>
            </div>
            <p className="mt-5 text-center text-xs text-muted">
              {mode === 'register'
                ? t('alreadyHaveAccount')
                : t('dontHaveAccount')}{' '}
              <button
                className="text-primary-text underline underline-offset-2 transition-colors hover:text-primary"
                onClick={toggleMode}
                type="button"
              >
                {mode === 'register' ? t('login') : t('register')}
              </button>
            </p>
            <p className="mt-4 text-center text-xs leading-5 text-subtle">
              <Trans
                components={{
                  privacyLink: (
                    <a
                      className="text-primary-text underline underline-offset-2 transition-colors hover:text-primary"
                      href="https://mylostudio.ai/ru/legal/privacy"
                      rel="noopener noreferrer"
                      target="_blank"
                    />
                  ),
                  termsLink: (
                    <a
                      className="text-primary-text underline underline-offset-2 transition-colors hover:text-primary"
                      href="https://mylostudio.ai/ru/legal/offer"
                      rel="noopener noreferrer"
                      target="_blank"
                    />
                  ),
                }}
                i18nKey="terms"
                t={t}
              />
            </p>
          </div>
        </div>
      </section>
    </main>
  );
};

const Metric = ({
  value,
  label,
}: {
  value: string;
  label: string;
}): ReactElement => (
  <div className="bg-elevated p-5">
    <strong className="block font-mono text-sm text-foreground">{value}</strong>
    <span className="mt-1 block text-xs text-muted">{label}</span>
  </div>
);

export const AuthCallbackPage = (): ReactElement => {
  const { provider = '' } = useParams();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { t } = useTranslation('auth');
  const status = params.get('status') === 'cancelled' ? 'cancelled' : 'failed';
  return (
    <main className="grid min-h-screen place-items-center bg-surface p-6 text-foreground">
      <div className="w-full max-w-sm rounded-3xl border border-border bg-elevated p-8 text-center shadow-[var(--studio-shadow)]">
        <BrandLogo className="justify-center" />
        <div className="my-8 flex justify-center text-primary-text">
          <span className="text-2xl">!</span>
        </div>
        <h1 className="font-semibold text-foreground">
          {t(status, { provider: provider.toUpperCase() })}
        </h1>
        <Button className="mt-6" onClick={() => navigate('/login')}>
          {t('google')}
        </Button>
      </div>
    </main>
  );
};
